/**
 * usePdfObjectUrl - the ONLY src that may be handed to an iframe.
 *
 * An object URL inherits OUR origin, so anything rendered from a `blob:` URL runs as a
 * page on app.brokly.ch, where the broker's token sits in localStorage. The browser
 * picks its renderer from `blob.type` - the response Content-Type, which the api copies
 * from the `document` row. Documents arrive as email attachments, so neither that type
 * nor the filename is ours to trust: a row holding `text/html` would be served as HTML,
 * told to render inline, and executed.
 *
 * So the bytes are verified on arrival, against the value the browser actually obeys.
 * Checking metadata first (`isInlinePreviewable`) gates the FETCH; this gates the
 * RENDER. Both are needed - metadata disagreeing with the bytes IS the attack.
 *
 * This is the ONLY guard, not a second layer. Validating at storage was dropped
 * (2026-09-07, with Jevin): it would have to be repeated in every intake path, so the
 * next one forgets it, and it could never cover documents already stored. Nothing is
 * refused STORAGE by any of this - a file the app will not render inline is still
 * stored, listed and downloadable.
 *
 * A separate hook rather than a flag so the guarantee lives in the name: no call site
 * can forget it, none can pass a type that re-opens the hole. Same posture as
 * `sanitizeEmailHtml`. Use `useDocumentBlobUrl` directly ONLY where the result is never
 * rendered as a page.
 *
 * [Source: apps/web/src/lib/sanitize-email-html.ts] (named-guarantee precedent)
 */
import { useDocumentBlobUrl, type DocumentBlobUrl } from './useDocumentBlobUrl';

/** A document's bytes as an iframe src, or null when they are not a PDF. */
export function usePdfObjectUrl(id: string | null, mockUrl?: string | null): DocumentBlobUrl {
  const { src, isLoading, isError, blobType } = useDocumentBlobUrl(id, mockUrl);
  // A STATED type that is not a PDF is the refusal, the empty string included: "the api
  // sent no Content-Type" is not a PDF either. A null type is not a refusal - there is
  // no blob to judge (mock mode, or bytes not yet arrived) and `src` is null anyway.
  const wrongType = blobType !== null && blobType !== 'application/pdf';
  return {
    src: wrongType ? null : src,
    isLoading,
    isError: isError || wrongType,
    blobType,
  };
}
